import type { AdminCostsByUserResponse, AdminTrainingStatusResponse } from "@/api/admin-api"

import { api } from "@/lib/api-client"

// NOTE: Document field names (`ambassadorOid`, `ambassadorName`, `ambassadorEmail`)
// are preserved because they match the stored Cosmos document schema. Only API
// paths and TS symbols were rebranded to "Local AI Lead".
export type LocalAiLeadMeResponse = {
  ambassadorOid: string
  ambassadorName?: string
  ambassadorEmail?: string
  assignedUsers: Array<{ oid: string; name: string; email: string }>
  assignedGroups?: string[]
  costModel?: "legacy" | "shared_annual_v1"
  costModelEnabledFrom?: string | null
  reportingCurrency?: "USD" | "SEK" | "EUR"
  assignedVisitsEnabled?: boolean
  mapped: boolean
}

export type LocalAiLeadSummaryResponse = {
  rangeDays: number | null
  currency: string
  totalCost: number
  totalTokens: number
  totalRequests: number
  activeUsers: number
  avgTrainingPct: number
  assignedUserCount: number
}

export async function getLocalAiLeadMe(): Promise<LocalAiLeadMeResponse> {
  const response = await api.get("/local-ai-lead/me")
  return response.data || response
}

export async function getLocalAiLeadCosts(params: {
  range?: number
  startDate?: string
  endDate?: string
  currency?: string
}): Promise<AdminCostsByUserResponse> {
  const query = new URLSearchParams()
  if (params.range !== undefined) query.append("range", String(params.range))
  if (params.startDate) query.append("start_date", params.startDate)
  if (params.endDate) query.append("end_date", params.endDate)
  if (params.currency) query.append("currency", params.currency)
  const qs = query.toString()
  const response = await api.get(`/local-ai-lead/costs/users${qs ? `?${qs}` : ""}`)
  return response.data || response
}

export async function getLocalAiLeadTraining(): Promise<AdminTrainingStatusResponse> {
  const response = await api.get("/local-ai-lead/training/status")
  return response.data || response
}

export type LocalAiLeadTrendPoint = {
  date: string
  tokensIn: number
  tokensOut: number
  tokensTotal: number
  requests: number
  errors: number
  costUSD: number
}

export type LocalAiLeadTrendResponse = {
  rangeDays: number | null
  points: LocalAiLeadTrendPoint[]
}

export async function getLocalAiLeadTrend(params: {
  range?: number
  startDate?: string
  endDate?: string
  currency?: string
}): Promise<LocalAiLeadTrendResponse> {
  const query = new URLSearchParams()
  if (params.range !== undefined) query.append("range", String(params.range))
  if (params.startDate) query.append("start_date", params.startDate)
  if (params.endDate) query.append("end_date", params.endDate)
  if (params.currency) query.append("currency", params.currency)
  const qs = query.toString()
  const response = await api.get(`/local-ai-lead/costs/trend${qs ? `?${qs}` : ""}`)
  return response.data || response
}

export type LocalAiLeadHeadcountRow = {
  groupValue: string
  employees: number | null
  activeUsers: number
  adoption: number | null
  updatedAt?: string | null
  updatedBy?: string | null
}

export type LocalAiLeadCurvePoint = {
  activeUsers: number
  annualCostPerUser: number
}

export type LocalAiLeadAdoptionCurve = {
  points: LocalAiLeadCurvePoint[]
  todayUsers: number
  todayAnnualCostPerUser: number | null
  benchmarkAnnualPerSeat: number | null
}

export type LocalAiLeadDashboardSections = {
  requests_per_user: boolean
  shared_cost_lines: boolean
  user_full_cost: boolean
  headcount_adoption: boolean
  adoption_curve: boolean
  group_overview: boolean
}

export type LocalAiLeadCostBreakdownResponse = {
  costModel?: "shared_annual_v1"
  costMonth?: string
  groupValue?: string
  availableGroups?: string[]
  usageRange?: { startDate: string | null; endDate: string | null; rangeDays: number | null }
  siteUsageMonthly?: number
  siteSharedMonthly?: number
  siteTotalMonthly?: number
  siteActiveUsers?: number
  costPerUserMonthly?: number | null
  siteUserCosts?: Array<{
    userId: string
    email?: string | null
    usageMonthly: number
    sharedMonthly: number
    fullMonthly: number
  }>
  rangeDays: number
  currency: string
  globalActiveUsers: number
  assignedActiveUsers: number
  assignedUserCount: number
  leadDirectUsageMonthly: number
  monthlyCostPerUser: number | null
  monthlyCostTotal: number
  fixedCostsConfigured: boolean
  sections?: Partial<LocalAiLeadDashboardSections>
  allocatedSharedMonthly?: number
  sharedPerUserMonthly?: number
  requestsPerUser?: number | null
  requestsPerUser3m?: number | null
  headcounts?: LocalAiLeadHeadcountRow[]
  adoptionCurve?: LocalAiLeadAdoptionCurve
}

export async function getLocalAiLeadCostBreakdown(params: {
  range?: number
  startDate?: string
  endDate?: string
  currency?: string
  groupValue?: string
}): Promise<LocalAiLeadCostBreakdownResponse> {
  const query = new URLSearchParams()
  if (params.range !== undefined) query.append("range", String(params.range))
  if (params.startDate) query.append("start_date", params.startDate)
  if (params.endDate) query.append("end_date", params.endDate)
  if (params.currency) query.append("currency", params.currency)
  if (params.groupValue) query.append("groupValue", params.groupValue)
  const qs = query.toString()
  const response = await api.get(`/local-ai-lead/cost-breakdown${qs ? `?${qs}` : ""}`)
  return response.data || response
}

export type LocalAiLeadCompanyUsageResponse = AdminCostsByUserResponse & {
  groupValue: string
  points: LocalAiLeadTrendPoint[]
  avgTrainingPct: number
}

export async function getLocalAiLeadCompanyUsage(params: {
  startDate: string
  endDate: string
  groupValue: string
}): Promise<LocalAiLeadCompanyUsageResponse> {
  const query = new URLSearchParams({
    start_date: params.startDate,
    end_date: params.endDate,
    groupValue: params.groupValue,
  })
  const response = await api.get(`/local-ai-lead/company-usage?${query.toString()}`)
  return response.data || response
}

export async function updateLocalAiLeadHeadcount(body: {
  groupValue: string
  employees: number
}): Promise<{ groupValue: string; employees: number }> {
  const response = await api.put("/local-ai-lead/headcount", body)
  return response.data || response
}

export async function getLocalAiLeadSummary(params: {
  range?: number
  startDate?: string
  endDate?: string
  currency?: string
}): Promise<LocalAiLeadSummaryResponse> {
  const query = new URLSearchParams()
  if (params.range !== undefined) query.append("range", String(params.range))
  if (params.startDate) query.append("start_date", params.startDate)
  if (params.endDate) query.append("end_date", params.endDate)
  if (params.currency) query.append("currency", params.currency)
  const qs = query.toString()
  const response = await api.get(`/local-ai-lead/summary${qs ? `?${qs}` : ""}`)
  return response.data || response
}

// =============================================================================
// Budget-request approvals (fair use round 2, #3087 / spec R11)
//
// A local AI lead may approve or deny budget requests from THEIR assigned
// users, inside a platform-set monthly SEK allowance. Every rule — mapping,
// self-approval, allowance, month lock — is enforced by the backend; the types
// below exist so the page can show what is left and bound its own input
// instead of discovering the ceiling through a 409.
// =============================================================================

/** Status filter accepted by `GET /api/local-ai-lead/requests`. */
export type LocalAiLeadRequestStatus = "pending" | "approved" | "denied"

/** One user's budget request, as `BudgetRequest.to_dict()` puts it on the wire. */
export type LocalAiLeadBudgetRequest = {
  id: string
  partition_key: string
  kind: string
  user_id: string
  year_month: string
  motivation: string | null
  status: LocalAiLeadRequestStatus
  resolved_by: string | null
  resolved_at: string | null
  ts: string
  /** Added by the route's Graph/Cosmos profile enrichment — best effort. */
  email?: string
  profile?: {
    name?: string
    email?: string
    jobTitle?: string
    department?: string
    officeLocation?: string
    company?: string
  }
}

/**
 * This lead's month allowance (`FairUseAdmin.lead_allowance_status`).
 *
 * `max_blocks` is the backend's own answer to "how many blocks fit in what is
 * left" — the approve control uses it directly rather than recomputing
 * `remaining_sek / block_cap_sek`, so the UI and the 409 cannot disagree.
 */
export type LocalAiLeadAllowance = {
  year_month: string
  allowance_sek: number
  used_sek: number
  remaining_sek: number
  /** SEK of Azure budget one approved block hands the user. */
  block_cap_sek: number
  max_blocks: number
}

export type LocalAiLeadRequestsResponse = {
  year_month: string
  mapped: boolean
  assignedUserCount: number
  requests: LocalAiLeadBudgetRequest[]
  count: number
  pending_count: number
  allowance: LocalAiLeadAllowance
}

/** The grant a lead approval writes (`Grant.to_dict()`). */
export type LocalAiLeadGrant = {
  id: string
  user_id: string
  year_month: string
  blocks: number
  block_cap_sek: number
  granted_by: string
  reason: string | null
  request_id: string | null
  ts: string
  approved_by_role: "admin" | "lead"
  budget_owner: { oid: string; department?: string | null } | null
}

/**
 * Stable `code` values the lead endpoints answer with. Kept as a union rather
 * than free strings so a renamed backend code fails typecheck here instead of
 * silently falling through to the generic error toast.
 */
export type LocalAiLeadRequestErrorCode =
  | "fair_use_not_configured"
  | "invalid_request"
  | "request_not_found"
  | "request_not_pending"
  | "month_locked"
  | "month_in_future"
  | "lead_not_mapped"
  | "lead_user_not_assigned"
  | "lead_self_approval"
  | "lead_allowance_exceeded"

export async function getLocalAiLeadRequests(params: {
  status?: LocalAiLeadRequestStatus
  month?: string
}): Promise<LocalAiLeadRequestsResponse> {
  const query = new URLSearchParams()
  if (params.status) query.append("status", params.status)
  if (params.month) query.append("month", params.month)
  const qs = query.toString()
  const response = await api.get(`/local-ai-lead/requests${qs ? `?${qs}` : ""}`)
  return response.data || response
}

export async function approveLocalAiLeadRequest(args: {
  requestId: string
  blocks: number
  month?: string
}): Promise<{ success: boolean; grant: LocalAiLeadGrant }> {
  const response = await api.post(
    `/local-ai-lead/requests/${encodeURIComponent(args.requestId)}/approve`,
    args.month ? { blocks: args.blocks, month: args.month } : { blocks: args.blocks }
  )
  return response.data || response
}

export async function denyLocalAiLeadRequest(args: {
  requestId: string
  reason?: string
  month?: string
}): Promise<{ success: boolean; request: LocalAiLeadBudgetRequest }> {
  const body: Record<string, string> = {}
  const reason = args.reason?.trim()
  if (reason) body.reason = reason
  if (args.month) body.month = args.month
  const response = await api.post(
    `/local-ai-lead/requests/${encodeURIComponent(args.requestId)}/deny`,
    body
  )
  return response.data || response
}
